import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { motion } from 'framer-motion'
import { Calendar, MapPin, Users, Video, Clock, ChevronLeft, Loader2, User, Download } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import api from '@/api/client'
import { toast } from '@/components/ui/toaster'
import { formatDate } from '@/lib/utils'
import { sanitizeExternalUrl } from '@/lib/urlSanitizer'
import { useAuthStore } from '@/stores/authStore'
import JitsiMeet from '@/components/JitsiMeet'

interface Speaker {
  name: string
  title: string
  bio: string
  avatar: string
}

interface AgendaItem {
  time: string
  title: string
  description: string
}

export default function EventDetail() {
  const { t } = useTranslation()
  const { id } = useParams()
  const { user, isAuthenticated } = useAuthStore()
  const queryClient = useQueryClient()
  const [activeTab, setActiveTab] = useState<'details' | 'speakers' | 'agenda' | 'participants'>('details')
  const [showJitsi, setShowJitsi] = useState(false)

  const { data: event, isLoading } = useQuery({
    queryKey: ['event', id],
    queryFn: () => api.get(`/events/${id}`).then(r => r.data),
    enabled: !!id
  })

  const { data: participants } = useQuery({
    queryKey: ['event-participants', id],
    queryFn: () => api.get(`/events/${id}/participants`).then(r => r.data),
    enabled: !!id && activeTab === 'participants'
  })

  const handleDownloadIcs = async () => {
    if (!event?._id) return
    try {
      const res = await api.get(`/events/${event._id}/ics`, { responseType: 'blob' })
      const blob = new Blob([res.data], { type: 'text/calendar' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `nyxeverix-event-${event._id}.ics`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (err: any) {
      toast({ title: err.response?.data?.error || t('common.error'), variant: 'destructive' })
    }
  }

  const isRegistered = event?.participants?.some((p: any) => p.userId?._id === user?.id || p.userId === user?.id)
  // F1.12: Sanitize admin-supplied meeting URL to prevent javascript: XSS
  const safeMeetingUrl = event?.meetingUrl ? sanitizeExternalUrl(event.meetingUrl) : ''

  // Determine Jitsi room name: prefer admin-supplied Jitsi URL, else auto-generate
  const jitsiRoomName = (() => {
    if (safeMeetingUrl && safeMeetingUrl.includes('8x8.vc')) {
      try {
        const u = new URL(safeMeetingUrl)
        return u.pathname.replace(/^\//, '') || `nyxeverix-event-${id}`
      } catch {
        return `nyxeverix-event-${id}`
      }
    }
    return `nyxeverix-event-${id}`
  })()

  const registerMutation = useMutation({
    mutationFn: () => api.post('/events/register', { eventId: id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['event', id] })
      toast({ title: t('events.registered') })
    },
    onError: (err: any) => toast({ title: err.response?.data?.error || t('common.error'), variant: 'destructive' })
  })

  const leaveMutation = useMutation({
    mutationFn: () => api.post(`/events/${id}/leave`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['event', id] })
      toast({ title: t('events.left') })
    },
    onError: (err: any) => toast({ title: err.response?.data?.error || t('common.error'), variant: 'destructive' })
  })

  if (isLoading) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-12 flex justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    )
  }

  if (!event) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-12 text-center">
        <p className="text-muted-foreground">{t('events.notFound')}</p>
        <Link to="/events" className="text-primary hover:underline mt-4 inline-block">
          {t('events.backToEvents')}
        </Link>
      </div>
    )
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-6">
      <Link to="/events" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4">
        <ChevronLeft className="w-4 h-4" /> {t('events.backToEvents')}
      </Link>

      {event.image && (
        <div className="w-full h-48 md:h-64 rounded-2xl overflow-hidden mb-6">
          <img
            src={event.image}
            alt={event.title}
            className="w-full h-full object-cover bg-secondary"
            loading="lazy"
            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
          />
        </div>
      )}

      <div className="flex items-start justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold mb-2">{event.title}</h1>
          <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
            <span className="flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              {formatDate(event.date)}
            </span>
            {event.location && (
              <span className="flex items-center gap-1">
                <MapPin className="w-4 h-4" />
                {event.location}
              </span>
            )}
            {event.isOnline && (
              <span className="flex items-center gap-1 text-primary">
                <Video className="w-4 h-4" />
                {t('events.online')}
              </span>
            )}
            <span className="flex items-center gap-1">
              <Users className="w-4 h-4" />
              {event.participants?.length || 0}
              {event.maxParticipants > 0 && ` / ${event.maxParticipants}`}
            </span>
          </div>
        </div>

        <div className="flex flex-col items-end gap-2">
          {isAuthenticated && (
            <>
              {isRegistered ? (
                <Button variant="outline" onClick={() => leaveMutation.mutate()} disabled={leaveMutation.isPending}>
                  {t('events.leave')}
                </Button>
              ) : (
                <Button onClick={() => registerMutation.mutate()} disabled={registerMutation.isPending || (event.maxParticipants > 0 && event.participants?.length >= event.maxParticipants)}>
                  {t('events.register')}
                </Button>
              )}
            </>
          )}
          <button
            type="button"
            onClick={handleDownloadIcs}
            className="text-xs flex items-center gap-1 text-muted-foreground hover:text-primary transition-colors"
            aria-label={t('events.addToCalendar')}
          >
            <Download className="w-3 h-3" /> {t('events.addToCalendar')}
          </button>
        </div>
      </div>

      {event.isOnline && isRegistered && (
        <div className="mb-4">
          {!showJitsi ? (
            <div className="flex items-center gap-3">
              {/* If admin provided a non-Jitsi URL, still show external link */}
              {safeMeetingUrl && !safeMeetingUrl.includes('8x8.vc') ? (
                <a
                  href={safeMeetingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors"
                >
                  <Video className="w-4 h-4" />
                  {t('events.joinMeeting')}
                </a>
              ) : (
                <Button onClick={() => setShowJitsi(true)} className="gap-2">
                  <Video className="w-4 h-4" />
                  {t('events.joinMeeting')}
                </Button>
              )}
            </div>
          ) : (
            <JitsiMeet
              roomName={jitsiRoomName}
              displayName={user?.username || ''}
              onClose={() => setShowJitsi(false)}
            />
          )}
        </div>
      )}

      <div className="flex gap-1 mb-6 p-1 rounded-xl bg-secondary/50">
        {(['details', 'speakers', 'agenda', 'participants'] as const).map(tab => (
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === tab}
            aria-label={t(`events.tabs.${tab}`)}
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === tab ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {t(`events.tabs.${tab}`)}
          </button>
        ))}
      </div>

      {activeTab === 'details' && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <div className="p-5 rounded-2xl bg-card border border-border">
            <h2 className="font-semibold mb-3">{t('events.about')}</h2>
            <p className="text-sm text-muted-foreground whitespace-pre-wrap">{event.description}</p>
            <div className="mt-4 flex flex-wrap gap-2">
              {event.category && (
                <span className="text-xs px-2 py-1 rounded-full bg-primary/10 text-primary">
                  {t('events.categories.' + event.category)}
                </span>
              )}
              {event.status && (
                <span className="text-xs px-2 py-1 rounded-full bg-secondary text-muted-foreground">
                  {t('events.status.' + event.status)}
                </span>
              )}
            </div>
          </div>
        </motion.div>
      )}

      {activeTab === 'speakers' && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-3">
          {event.speakers?.length > 0 ? event.speakers.map((speaker: Speaker, i: number) => (
            <div key={i} className="p-4 rounded-2xl bg-card border border-border flex items-start gap-4">
              <Avatar className="w-14 h-14">
                <AvatarImage src={speaker.avatar} />
                <AvatarFallback>{speaker.name[0]}</AvatarFallback>
              </Avatar>
              <div>
                <h3 className="font-semibold">{speaker.name}</h3>
                <p className="text-sm text-primary">{speaker.title}</p>
                <p className="text-sm text-muted-foreground mt-1">{speaker.bio}</p>
              </div>
            </div>
          )) : (
            <p className="text-muted-foreground text-center py-8">{t('events.noSpeakers')}</p>
          )}
        </motion.div>
      )}

      {activeTab === 'agenda' && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-3">
          {event.agenda?.length > 0 ? event.agenda.map((item: AgendaItem, i: number) => (
            <div key={i} className="p-4 rounded-2xl bg-card border border-border flex items-start gap-4">
              <div className="flex items-center gap-1 text-primary font-medium min-w-[60px]">
                <Clock className="w-4 h-4" />
                {item.time}
              </div>
              <div>
                <h3 className="font-semibold">{item.title}</h3>
                <p className="text-sm text-muted-foreground">{item.description}</p>
              </div>
            </div>
          )) : (
            <p className="text-muted-foreground text-center py-8">{t('events.noAgenda')}</p>
          )}
        </motion.div>
      )}

      {activeTab === 'participants' && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {participants?.length > 0 ? participants.map((p: any) => (
            <Link
              key={p.userId?._id || p.userId}
              to={`/profile/${p.userId?.username || ''}`}
              className="p-3 rounded-xl bg-card border border-border flex items-center gap-3 hover:border-primary/20 transition-colors"
            >
              <Avatar className="w-8 h-8">
                <AvatarImage src={p.userId?.avatar} />
                <AvatarFallback><User className="w-4 h-4" /></AvatarFallback>
              </Avatar>
              <span className="text-sm font-medium truncate">{p.userId?.username || t('common.user')}</span>
            </Link>
          )) : (
            <p className="text-muted-foreground text-center py-8 col-span-full">{t('events.noParticipants')}</p>
          )}
        </motion.div>
      )}
    </div>
  )
}
